// Steps like the second hand of the footer clock; smooth scrolling has no fixed duration or curve
function scrollLikeSecondHand(element, left, duration) {
  const target = Math.max(0, Math.min(left, element.scrollWidth - element.clientWidth));
  cancelAnimationFrame(element.secondHandFrame);
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    element.scrollLeft = target;
    return;
  }

  const token = getComputedStyle(document.documentElement).getPropertyValue('--duration-default').trim();
  duration ||= parseFloat(token) * (token.endsWith('ms') ? 1 : 1000) || 200;
  const start = element.scrollLeft;
  const startTime = performance.now();
  // ease-out = cubic-bezier(0, 0, 0.58, 1): find the curve parameter for the time, return its progress
  const ease = (time) => {
    let low = 0;
    let high = 1;
    let t = time;
    for (let i = 0; i < 20; i++) {
      t = (low + high) / 2;
      if (1.74 * (1 - t) * t * t + t * t * t < time) low = t;
      else high = t;
    }
    return 3 * (1 - t) * t * t + t * t * t;
  };
  const step = (now) => {
    const time = Math.min((now - startTime) / duration, 1);
    element.scrollLeft = start + (target - start) * ease(time);
    if (time < 1) {
      element.secondHandFrame = requestAnimationFrame(step);
    } else {
      delete element.secondHandTarget;
    }
  };
  element.secondHandTarget = target;
  element.secondHandFrame = requestAnimationFrame(step);
}

function secondHandPosition(element) {
  return element.secondHandTarget ?? element.scrollLeft;
}

if (!customElements.get('slider-scale')) {
  class SliderScale extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = [...this.track.querySelectorAll('[data-slide]')];
      this.controls = this.querySelector('[data-slider-controls]');
      this.ticks = [...this.querySelectorAll('[data-slider-tick]')];
      this.label = this.querySelector('[data-slider-label]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');

      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.prev.addEventListener('click', () => this.go(-1));
      this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
      this.track.addEventListener('focusin', (event) => {
        event.target.closest('[data-slide]')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
      });
      this.update();
    }

    startDrag(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.track.scrollWidth <= this.track.clientWidth + 1) return;

      const startX = event.clientX;
      const startLeft = this.track.scrollLeft;
      let dragging = false;

      const move = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        if (!dragging && Math.abs(delta) < 5) return;
        if (!dragging) {
          dragging = true;
          cancelAnimationFrame(this.track.secondHandFrame);
          delete this.track.secondHandTarget;
          this.track.setPointerCapture(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = startLeft - delta;
      };

      const end = () => {
        this.track.removeEventListener('pointermove', move);
        this.track.removeEventListener('pointerup', end);
        this.track.removeEventListener('pointercancel', end);
        if (!dragging) return;

        this.track.classList.remove('is-dragging');
        const suppressClick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        this.track.addEventListener('click', suppressClick, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', suppressClick, { capture: true }));

        const position = this.track.scrollLeft / this.step;
        const index = this.track.scrollLeft > startLeft ? Math.ceil(position - 0.2) : Math.floor(position + 0.2);
        scrollLikeSecondHand(this.track, index * this.step, Number(this.dataset.duration));
      };

      this.track.addEventListener('pointermove', move);
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);
    }

    get step() {
      return this.slides.length > 1 ? this.slides[1].offsetLeft - this.slides[0].offsetLeft : this.track.clientWidth;
    }

    go(direction) {
      const index = Math.round(secondHandPosition(this.track) / this.step) + direction;
      scrollLikeSecondHand(this.track, index * this.step, Number(this.dataset.duration));
    }

    update() {
      const overflow = this.track.scrollWidth > this.track.clientWidth + 1;
      this.controls.hidden = !overflow;
      this.track.classList.toggle('is-draggable', overflow);
      if (!overflow) return;

      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
      const index = atEnd ? this.slides.length - 1 : Math.round(this.track.scrollLeft / this.step);

      this.ticks.forEach((tick, i) => tick.classList.toggle('pagination-scale__tick--current', i === index));
      const label = `${index + 1} ${this.label.dataset.of} ${this.slides.length}`;
      if (this.label.textContent !== label) this.label.textContent = label;
      this.prev.disabled = atStart;
      this.next.disabled = atEnd;
    }
  }

  customElements.define('slider-scale', SliderScale);
}
